<script>
  import { onMount } from "svelte";

  import leaflet from "leaflet";
  import geoJSON from "@mapbox/togeojson";

  import classifyPoint from "robust-point-in-polygon";

  export let practice;

  let areaData;
  fetch(practice.dataURL)
    .then(res => res.text())
    .then(text => (areaData = text));

  window.a = areaData;

  export let postcode;
  export let status;

  let catchmentKML, catchmentArea;

  const statusMessages = {
    noPostcode: "👆Enter a postcode.",
    netError: "❌ Network error.",
    intError: "❌ Internal error.",
    invalid: "❓Invalid postcode",
    outside: "🛑 Postcode not in catchment area.",
    inside: "✅ Postcode in catchment area!"
  };

  // Map

  var map, practiceMarker, postcodeMarker, distline;
  var markerIcon = leaflet.icon({
    iconUrl: "marker-icon.svg",
    iconSize: [100, 100]
  });
  var markerIconTick = leaflet.icon({
    iconUrl: "marker-icon-tick.svg",
    iconSize: [100, 100]
  });
  var markerIconCross = leaflet.icon({
    iconUrl: "marker-icon-cross.svg",
    iconSize: [100, 100]
  });

  const attribution =
    'Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://www.openstreetmap.org/copyright">ODbL</a>.';

  onMount(() => {
    map = leaflet.map("mapid").setView(practice.latlon, 13);

    leaflet
      .tileLayer(
        "https://stamen-tiles.a.ssl.fastly.net/terrain/{z}/{x}/{y}.png",
        {
          attribution,
          maxZoom: 15
        }
      )
      .addTo(map);

    practiceMarker = leaflet
      .marker(practice.latlon, { icon: markerIcon })
      .addTo(map);

    postcodeMarker = leaflet
      .marker([51.5001, -0.09001], { icon: markerIcon })
      .addTo(map);

    distline = leaflet.polyline([]).addTo(map);
  });

  // Area check & display
  const parser = new DOMParser();
  var areaLayer;

  $: {
    // Parse KML string into DOM
    if (areaData && map) {
      catchmentKML = parser.parseFromString(areaData, "text/xml");

      // Extract catchmentArea coord data
      catchmentArea = catchmentKML
        .querySelector("Placemark coordinates")
        .innerHTML.trim()
        .split("\n")
        .map(a =>
          a
            .trim()
            .split(",")
            .slice(0, 2)
            .map(Number)
        )
        .reverse();

      // Display KML
      areaLayer = leaflet.geoJSON(geoJSON.kml(catchmentKML)).addTo(map);
    }
  }

  // Point check & display

  $: {
    if (postcode) {
      fetch(`https://api.postcodes.io/postcodes/${postcode}`)
        .then(res => res.json())
        .then(res => {
          if (res.status === 200) {
            postcodeMarker.setOpacity(1);
            map.setView([res.result.latitude, res.result.longitude], 15);
            postcodeMarker.setLatLng([
              res.result.latitude,
              res.result.longitude
            ]);

            if (
              classifyPoint(catchmentArea, [
                res.result.longitude,
                res.result.latitude
              ]) <= 0
            ) {
              status.set(statusMessages.inside);
              postcodeMarker.setIcon(markerIconTick);

              distline.setLatLngs([]);
            } else {
              postcodeMarker.setIcon(markerIconCross);

              let closestPoint;

              var dist = catchmentArea.reduce((acc, lonlat) => {
                let val = leaflet.CRS.Earth.distance(
                  leaflet.latLng(...[...lonlat].reverse()),
                  leaflet.latLng(res.result.latitude, res.result.longitude)
                );

                if (val < acc) {
                  closestPoint = [...lonlat].reverse();
                }

                return Math.min(acc, val);
              }, Infinity);

              if (dist >= 1000) {
                dist = dist / 1000;
                dist = dist.toFixed(1) + "k";
              } else {
                dist = dist.toFixed(0);
              }

              status.set(
                `${statusMessages.outside} (≈${dist}m away from boundary)`
              );

              distline.setLatLngs([
                closestPoint,
                [res.result.latitude, res.result.longitude]
              ]);
            }
          } else if (res.status === 400) {
            status.set(statusMessages.noPostcode);

            postcodeMarker.setOpacity(0);
            distline.setLatLngs([]);
          } else if (res.status === 404) {
            status.set(statusMessages.invalid);

            postcodeMarker.setOpacity(0);
            distline.setLatLngs([]);
          }
        });
    } else {
      status.set(statusMessages.noPostcode);
      if (postcodeMarker) postcodeMarker.setOpacity(0);
    }
  }
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://unpkg.com/leaflet@1.5.1/dist/leaflet.css"
    integrity="sha512-xwE/Az9zrjBIphAcBb3F6JVqxf46+CDLwfLMHloNu6KEQCAWi6HcDUbeOfBIptF7tcCzusKFjFw2yuvEpDL9wQ=="
    crossorigin="" />
</svelte:head>

<div id="mapid" />
